import { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * Floating scroll controls, bottom-right, elegant styling.
 * - Up: hidden when at top of page
 * - Down: hidden when at end of page
 * - Scrolls ~50% viewport height per click
 * - Respects prefers-reduced-motion (jumps instantly if reduced)
 */
export default function FloatingScrollControls() {
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const vh = window.innerHeight;
      const doc = document.documentElement.scrollHeight;
      setAtTop(y < 16);
      setAtBottom(y + vh >= doc - 16);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const scrollBy = (dir) => {
    const step = Math.round(window.innerHeight * 0.5) * dir;
    window.scrollBy({
      top: step,
      left: 0,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  };

  // Hide entirely on very small screens to preserve mobile footprint
  return (
    <div
      className="fixed z-40 right-4 sm:right-6 bottom-6 sm:bottom-8 flex flex-col gap-2"
      data-testid="floating-scroll-controls"
      aria-hidden={atTop && atBottom}
    >
      <button
        type="button"
        onClick={() => scrollBy(-1)}
        aria-label="Scroll up"
        data-testid="floating-scroll-up"
        className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full grid place-items-center bg-matte text-ivory border border-gold/30 shadow-[0_10px_30px_-10px_rgba(17,17,17,0.5)] backdrop-blur-sm transition-[opacity,transform,background-color] duration-300 hover:bg-gold hover:text-matte hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-gold/60 ${
          atTop ? 'opacity-0 pointer-events-none translate-y-1' : 'opacity-100'
        }`}
      >
        <ChevronUp size={18} strokeWidth={1.6} />
      </button>
      <button
        type="button"
        onClick={() => scrollBy(1)}
        aria-label="Scroll down"
        data-testid="floating-scroll-down"
        className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full grid place-items-center bg-matte text-ivory border border-gold/30 shadow-[0_10px_30px_-10px_rgba(17,17,17,0.5)] backdrop-blur-sm transition-[opacity,transform,background-color] duration-300 hover:bg-gold hover:text-matte hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-gold/60 ${
          atBottom ? 'opacity-0 pointer-events-none translate-y-1' : 'opacity-100'
        }`}
      >
        <ChevronDown size={18} strokeWidth={1.6} />
      </button>
    </div>
  );
}
